/* eslint-disable max-lines */
import { gql } from 'graphql-request';

export const listInventoryItemsGql = gql`
  query listInventoryItems($filter: InventoryItemFilter) {
    listInventoryItems(filter: $filter) {
      inventoryItemId
      ndc
      productId
      isDefault
      drugName
      strength
      manufacturer
      fulfillingPharmacy {
        fulfillingPharmacyId
        name
        city
        state
      }
      quantityOnHand
      reorderQty
      stockStatus
      lowStockPercent
      criticalStockPercent
      leadTime
      isPhaseOut
      lotCount
      isExpiringSoon
      expiringSoonCount
      avgDailyDispense
      totalDispensed14d
      isLockForCount
      # POM-6087 - which drugs carry a claim, so the question can be answered from the
      # list instead of by opening every drug's lot drawer in turn. Positive magnitude;
      # the minus sign is rendered in the grid. NEVER added to quantityOnHand.
      unverifiedQty
      unverifiedLotCount
      hasUnverifiedClaim
    }
  }
`;

export const listInventoryByNdcGql = gql`
  query listInventoryByNdc($ndc: String!, $includeInactive: Boolean) {
    listInventoryByNdc(ndc: $ndc, includeInactive: $includeInactive) {
      ndc
      drugName
      manufacturer
      strength
      isActive
    }
  }
`;

// POM-5707 — every NDC of a product from the inventory master, including ones
// not stocked at any facility. listInventoryItems cannot serve this: it is
// driven by the per-facility inventory table, so unstocked NDCs never appear.
export const listProductNdcsGql = gql`
  query listProductNdcs($productId: String!) {
    listProductNdcs(productId: $productId) {
      ndc
      drugName
      strength
      manufacturer
      isDefault
      # POM-5974 — additive. The partner preference dialog has to stop a user
      # hard-locking a partner to a deactivated NDC, and needs to say why the
      # option is unavailable rather than failing the mutation after the pick.
      isActive
    }
  }
`;

// POM-5910 — manufacturer pick-list for the Add Inventory Item dialog. Served
// from core's manufacturer table (POM-6404, maintained from Inventory →
// Manufacturer Management), not from the inventory table, so it is the list of
// manufacturers that MAY be picked rather than the ones already in use.
export const listInventoryManufacturersGql = gql`
  query listInventoryManufacturers {
    listInventoryManufacturers
  }
`;

export const inventoryLotsByLotIdGql = gql`
  query inventoryLotsByLotId($lotId: String!) {
    inventoryLotsByLotId(lotId: $lotId) {
      inventoryLotId
      lotNumber
      productId
      ndc
      drugName
      strength
      expirationDate
      fulfillingPharmacyId
      receivedQty
      remainingQty
      unverifiedQty
    }
  }
`;

export const lotSummaryGql = gql`
  query LotSummary(
    $inventoryItemId: UUID!
    $ndc: String!
    $fulfillingPharmacyId: String!
    $filter: LotSummaryFilter
  ) {
    lotSummary(
      inventoryItemId: $inventoryItemId
      ndc: $ndc
      fulfillingPharmacyId: $fulfillingPharmacyId
      filter: $filter
    ) {
      inventoryItemId
      ndc
      fulfillingPharmacyId
      totalPhysicalQty
      totalLottedQty
      totalWipQty
      expiringSoonCount
      expiredWithStockCount
      # POM-6094 - reported on their own. NEVER add these into totalPhysicalQty or
      # totalLottedQty: the verified and unverified buckets are deliberately never
      # summed, and folding a claim into on-hand asserts stock the pharmacy has not got.
      totalUnverifiedQty
      unverifiedLotCount
      # Reserved to orders that have not shipped. Server-side across EVERY lot on the
      # item - a client-side sum of the lots array would miss a depleted lot with a live
      # reservation, which the default includeDepleted = false hides.
      totalWipQty
      # How much of totalWipQty is open unverified claims - the overlap of the two buckets.
      # WIP minus this is reserved real stock.
      totalUnverifiedWipQty
      # Claims waiting in Reversal Review (order sent back to queue, undone or cancelled).
      # Unverified minus in-WIP minus this has already shipped.
      totalUnverifiedPendingReviewQty
      lots {
        inventoryLotId
        lotNumber
        expirationDate
        remainingQty
        daysUntilExpiry
        isExpired
        isExpiringSoon
        firstReceivedAt
        lastMovementAt
        wipQty
        availableQty
        # Held as a positive magnitude server-side; availableQty stays remainingQty
        # alone, which is what keeps reported availability from ever going negative.
        unverifiedQty
        hasUnverifiedClaim
        unverifiedWipQty
        unverifiedPendingReviewQty
      }
    }
  }
`;

export const listInventoryReceivingLineSplitsGql = gql`
  query inventoryReceivingLineSplits(
    $filter: InventoryReceivingLineSplitFilter
    $limit: Int
    $offset: Int
  ) {
    inventoryReceivingLineSplits(filter: $filter, limit: $limit, offset: $offset) {
      items {
        splitId
        receivingLineId
        quantityExpected
        quantityVerified
        offage
        offageReasons {
          qty
          reasons
        }
        status
        fulfillingPharmacy {
          fulfillingPharmacyId
          name
        }
        verifiedBy
        verifiedAt
        createdAt
        updatedAt
      }
      totalCount
      limit
      offset
    }
  }
`;

export const createInventoryReceivingLineSplitsGql = gql`
  mutation createInventoryReceivingLineSplits($input: CreateInventoryReceivingLineSplitsInput!) {
    createInventoryReceivingLineSplits(input: $input) {
      splits {
        splitId
        receivingLineId
        quantityExpected
        quantityVerified
        offage
        status
        fulfillingPharmacy {
          fulfillingPharmacyId
          name
        }
        verifiedBy
        verifiedAt
        createdAt
        updatedAt
      }
    }
  }
`;

export const verifyInventoryReceivingLineSplitGql = gql`
  mutation verifyInventoryReceivingLineSplit($input: VerifyInventoryReceivingLineSplitInput!) {
    verifyInventoryReceivingLineSplit(input: $input) {
      split {
        splitId
        receivingLineId
        quantityExpected
        quantityVerified
        offage
        offageReasons {
          qty
          reasons
        }
        status
        fulfillingPharmacy {
          fulfillingPharmacyId
          name
        }
        verifiedBy
        verifiedAt
        createdAt
        updatedAt
      }
    }
  }
`;

export const updateInventoryReceivingLineSplitQuantityGql = gql`
  mutation UpdateInventoryReceivingLineSplitQuantity(
    $input: UpdateInventoryReceivingLineSplitQuantityInput!
  ) {
    updateInventoryReceivingLineSplitQuantity(input: $input) {
      split {
        splitId
        receivingLineId
        quantityExpected
        quantityVerified
        offage
        status
        fulfillingPharmacy {
          fulfillingPharmacyId
          name
        }
        verifiedBy
        verifiedAt
        createdAt
        updatedAt
      }
    }
  }
`;

export const upsertInventoryReceivingGql = gql`
  mutation upsertInventoryReceiving($input: UpsertInventoryReceivingInput!) {
    upsertInventoryReceiving(input: $input) {
      receiving {
        receivingId
        vendorName
        manifestReference
        receivedDate
        status
        notes
        createdAt
      }
    }
  }
`;

export const listAvailableLotsForDispenseGql = gql`
  query listAvailableLotsForDispense(
    $productId: String!
    $fulfillingPharmacyId: String!
    $filter: AvailableLotsForDispenseFilter
  ) {
    listAvailableLotsForDispense(
      productId: $productId
      fulfillingPharmacyId: $fulfillingPharmacyId
      filter: $filter
    ) {
      groups {
        ndc
        manufacturer
        drugName
        isDefault
        lots {
          inventoryLotId
          lotNumber
          expirationDate
          remainingQty
          daysUntilExpiry
        }
      }
      totalCount
      isLocked
    }
  }
`;

// POM-6083 - options for recording a lot the dispense picker cannot offer.
// The mirror image of listAvailableLotsForDispense: NDCs inventory knows for this
// product, plus the lots sitting at zero (or negative) quantity that the picker
// filters out but which still exist as rows.
export const listRecordableLotsForProductGql = gql`
  query listRecordableLotsForProduct($productId: String!, $fulfillingPharmacyId: String!) {
    listRecordableLotsForProduct(
      productId: $productId
      fulfillingPharmacyId: $fulfillingPharmacyId
    ) {
      ndcOptions {
        ndc
        drugName
        manufacturer
        isDefault
      }
      depletedLots {
        inventoryLotId
        lotNumber
        ndc
        expirationDate
        remainingQty
        unverifiedQty
        isExpired
        lastMovementAt
      }
      hasInventoryMaster
    }
  }
`;

export const listVendorsGql = gql`
  query listVendors($filter: VendorFilter, $sort: VendorSort, $limit: Int, $offset: Int) {
    listVendors(filter: $filter, sort: $sort, limit: $limit, offset: $offset) {
      items {
        vendorId
        vendor
        createdAt
        updatedAt
      }
      totalCount
      limit
      offset
    }
  }
`;

export const addVendorGql = gql`
  mutation addVendor($input: AddVendorInput!) {
    addVendor(input: $input) {
      vendor {
        vendorId
        vendor
        createdAt
        updatedAt
      }
    }
  }
`;

// POM-6405 — Inventory → Manufacturer Management.
export const listManufacturersGql = gql`
  query listManufacturers(
    $filter: ManufacturerFilter
    $sort: ManufacturerSort
    $limit: Int
    $offset: Int
  ) {
    listManufacturers(filter: $filter, sort: $sort, limit: $limit, offset: $offset) {
      items {
        manufacturerId
        manufacturer
        isDeleted
        createdAt
        updatedAt
      }
      totalCount
      limit
      offset
    }
  }
`;

export const addManufacturerGql = gql`
  mutation addManufacturer($input: AddManufacturerInput!) {
    addManufacturer(input: $input) {
      manufacturer {
        manufacturerId
        manufacturer
        isDeleted
        createdAt
        updatedAt
      }
    }
  }
`;

export const updateManufacturerGql = gql`
  mutation updateManufacturer($input: UpdateManufacturerInput!) {
    updateManufacturer(input: $input) {
      manufacturer {
        manufacturerId
        manufacturer
        isDeleted
        createdAt
        updatedAt
      }
    }
  }
`;

export const deleteManufacturerGql = gql`
  mutation deleteManufacturer($input: DeleteManufacturerInput!) {
    deleteManufacturer(input: $input) {
      manufacturer {
        manufacturerId
        manufacturer
        isDeleted
        createdAt
        updatedAt
      }
    }
  }
`;

export const listInventoryReceivingsGql = gql`
  query inventoryReceivings($limit: Int = 25, $offset: Int = 0, $filter: InventoryReceivingFilter) {
    inventoryReceivings(limit: $limit, offset: $offset, filter: $filter) {
      items {
        receivingId
        vendorName
        manifestReference
        receivedDate
        receivedAtPharmacy {
          fulfillingPharmacyId
          name
          city
          state
        }
        documentUrl
        status
        notes
        createdAt
        updatedAt
        lines {
          receivingLineId
          ndc
          manufacturer
          lotNumber
          bud
          quantityOrdered
          quantityShipped
          totalQuantityReceived
          splitCount
          pendingSplitCount
          offageReasons {
            qty
            reasons
          }
          createdAt
        }
      }
      totalCount
      limit
      offset
    }
  }
`;

export const listInventoryTransactionsGql = gql`
  query listInventoryTransactions(
    $ndc: String!
    $fulfillingPharmacyId: String
    $limit: Int
    $offset: Int
    $filter: InventoryTransactionFilter
  ) {
    listInventoryTransactions(
      ndc: $ndc
      fulfillingPharmacyId: $fulfillingPharmacyId
      limit: $limit
      offset: $offset
      filter: $filter
    ) {
      transactions {
        transactionId
        transactionType
        quantityChange
        quantityAfter
        wipQtyAfter
        unverifiedQtyChange
        unverifiedQtyAfter
        fulfillingPharmacyId
        lotNumber
        expirationDate
        referenceType
        referenceId
        originalOrderNdc
        orderId
        rxNumber
        productId
        notes
        createdTimestamp
        createdBy {
          pomUserId
          firstName
          lastName
          email
        }
      }
      total
    }
  }
`;

export const listLotAllocationsGql = gql`
  query listLotAllocations(
    $filter: LotAllocationFilter
    $sort: LotAllocationSort
    $limit: Int
    $offset: Int
  ) {
    listLotAllocations(filter: $filter, sort: $sort, limit: $limit, offset: $offset) {
      items {
        allocationId
        lotNumber
        ndc
        drugName
        manufacturer
        strength
        quantity
        bud
        availableQty
        status
        createdAt
        fulfillingPharmacy {
          fulfillingPharmacyId
          name
          line1
          line2
          city
          state
          zip
          npi
          phone
          pharmacist
        }
        reasons {
          eventType
          reasons
          qty
        }
        isInPhysicalCount
        # POM-6084 - this row is an unverified CLAIM, not a reservation against received
        # stock. Restocking it mints verified stock from a scan nobody confirmed, and the
        # confirmation is permanent, so the reviewer has to be told which kind it is.
        isUnverified
        # Non-null once ANY quantity on the allocation has been confirmed - a part-approved
        # claim must not be mistaken for a fresh one.
        verifiedAt
        # POM-6152 - the confirmed share of quantity. The open claim the reviewer is
        # deciding on is quantity - settledQty; the rest resolves as ordinary stock.
        settledQty
      }
      total
      limit
      offset
    }
  }
`;

export const lineItemActiveAllocationsGql = gql`
  query lineItemActiveAllocations($lineItemId: String!) {
    lineItemActiveAllocations(lineItemId: $lineItemId) {
      allocationId
      lineItemId
      lotNumber
      ndc
      drugName
      manufacturer
      strength
      quantity
      status
      bud
      availableQty
      createdAt
    }
  }
`;

export const discardAllocationOfLineItemsGql = gql`
  mutation discardAllocationOfLineItems($lineItemId: String!) {
    discardAllocationOfLineItems(lineItemId: $lineItemId) {
      success
      results {
        allocationId
        lotNumber
        quantity
        inventoryAdjusted
      }
    }
  }
`;

export const manualInventoryAdjustGql = gql`
  mutation manualInventoryAdjust($input: [ManualInventoryAdjustInput!]!) {
    manualInventoryAdjust(input: $input) {
      results {
        lot {
          inventoryLotId
          lotNumber
          remainingQty
          lastMovementAt
        }
      }
    }
  }
`;

export const updateInventoryReorderConfigGql = gql`
  mutation updateInventoryReorderConfig($input: UpdateInventoryReorderConfigInput!) {
    updateInventoryReorderConfig(input: $input) {
      inventoryItem {
        inventoryItemId
        lowStockPercent
        criticalStockPercent
        leadTime
        stockStatus
      }
    }
  }
`;

export const resolveLotAllocationsGql = gql`
  mutation resolveLotAllocations($input: ResolveLotAllocationsInput!) {
    resolveLotAllocations(input: $input) {
      results {
        allocationId
        lotNumber
        quantity
        status
        discardReasons {
          qty
          reasons
        }
        inventoryAdjusted
      }
      success
    }
  }
`;

export const inventoryAlertSummaryGql = gql`
  query inventoryAlertSummary($filter: InventoryAlertFilter) {
    inventoryAlertSummary(filter: $filter) {
      lowStock {
        total
        criticalCount
        lowStockCount
      }
      expiringSoon {
        total
        withinDays
      }
      lowDaysOfSupply {
        total
        thresholdDays
      }
      inPhysicalCount {
        lockedItems
        totalInventoryItems
      }
    }
  }
`;

export const listInPhysicalCountItemsGql = gql`
  query ListInPhysicalCountItems($filter: InventoryAlertFilter, $limit: Int, $offset: Int) {
    listInPhysicalCountItems(filter: $filter, limit: $limit, offset: $offset) {
      items {
        inventoryItemId
        ndc
        # POM-6112 — a locked product is expanded to every sibling NDC stocked under the same
        # productId at that facility. Siblings usually share a drugName, so productId is what
        # tells the reader the extra rows are one product, not duplicates.
        productId
        drugName
        manufacturer
        strength
        facilityName
        quantityOnHand
        lockedAt
        lockedByName
        # POM-6112 — true only for the NDC that is literally locked; every other row is a
        # sibling shown for context. Tells the banner which chip to badge.
        isLocked
      }
      totalCount
    }
  }
`;

export const listLowStockItemsGql = gql`
  query listLowStockItems($filter: InventoryAlertFilter, $limit: Int, $offset: Int) {
    listLowStockItems(filter: $filter, limit: $limit, offset: $offset) {
      items {
        inventoryItemId
        ndc
        drugName
        strength
        facilityName
        fulfillingPharmacyId
        currentQty
        stockStatus
        reorderPoint
        shortage
      }
      totalCount
      limit
      offset
    }
  }
`;

export const listExpiringSoonItemsGql = gql`
  query listExpiringSoonItems(
    $filter: InventoryAlertFilter
    $withinDays: Int
    $limit: Int
    $offset: Int
  ) {
    listExpiringSoonItems(
      filter: $filter
      withinDays: $withinDays
      limit: $limit
      offset: $offset
    ) {
      items {
        inventoryItemId
        ndc
        drugName
        strength
        facilityName
        fulfillingPharmacyId
        lots
        daysUntilExpiry
      }
      totalCount
      limit
      offset
    }
  }
`;

export const listLowDaysOfSupplyItemsGql = gql`
  query listLowDaysOfSupplyItems(
    $filter: InventoryAlertFilter
    $thresholdDays: Int
    $limit: Int
    $offset: Int
  ) {
    listLowDaysOfSupplyItems(
      filter: $filter
      thresholdDays: $thresholdDays
      limit: $limit
      offset: $offset
    ) {
      items {
        inventoryItemId
        ndc
        drugName
        strength
        facilityName
        fulfillingPharmacyId
        currentQty
        dailyDispensingRate
        daysRemaining
      }
      totalCount
      limit
      offset
    }
  }
`;

export const lockItemForPhysicalCountGql = gql`
  mutation LockItemForPhysicalCount($input: LockItemForPhysicalCountInput!) {
    lockItemForPhysicalCount(input: $input) {
      inventoryCountId
      inventoryItemId
      systemQty
      lockedAt
    }
  }
`;

export const unlockItemFromPhysicalCountGql = gql`
  mutation UnlockItemFromPhysicalCount($input: UnlockItemFromPhysicalCountInput!) {
    unlockItemFromPhysicalCount(input: $input) {
      success
      inventoryCountId
    }
  }
`;

export const bulkLockItemsForPhysicalCountGql = gql`
  mutation BulkLockItemsForPhysicalCount($items: [BulkLockItemInput!]!) {
    bulkLockItemsForPhysicalCount(items: $items) {
      totalRequested
      locked {
        inventoryItemId
        status
      }
      skipped {
        inventoryItemId
        status
        reason
      }
      failed {
        inventoryItemId
        status
        reason
      }
    }
  }
`;

export const bulkUnlockItemsFromPhysicalCountGql = gql`
  mutation BulkUnlockItemsFromPhysicalCount($inventoryItemIds: [String!]!) {
    bulkUnlockItemsFromPhysicalCount(inventoryItemIds: $inventoryItemIds) {
      totalRequested
      unlocked {
        inventoryItemId
        status
      }
      skipped {
        inventoryItemId
        status
        reason
      }
      failed {
        inventoryItemId
        status
        reason
      }
    }
  }
`;

export const createCountGql = gql`
  mutation CreateCount($input: CreateCountInput!) {
    createCount(input: $input) {
      inventoryCountId
      inventoryItemId
      systemQty
      countedQty
      varianceQty
      status
    }
  }
`;

export const listCountsGql = gql`
  query ListCounts($filter: InventoryCountFilter, $limit: Int, $offset: Int) {
    listCounts(filter: $filter, limit: $limit, offset: $offset) {
      counts {
        inventoryCountId
        inventoryItemId
        systemQty
        countedQty
        varianceQty
        status
        countedBy
        countedByName
        notes
        createdTimestamp
        modifiedTimestamp
      }
      totalCount
      limit
      offset
    }
  }
`;

export const listLockedInventoryItemsGql = gql`
  query listLockedInventoryItems($fulfillingPharmacyIds: [String!]!) {
    listLockedInventoryItems(fulfillingPharmacyIds: $fulfillingPharmacyIds) {
      ndc
      fulfillingPharmacyId
      drugName
      strength
      productId
      # POM-6112 — sibling NDCs of a locked product come back here too, and they usually carry
      # the same drugName. Manufacturer is what distinguishes those rows on screen.
      manufacturer
      # POM-6112 — true only for the NDC that is literally locked; every other row is a
      # sibling shown for context. Tells the banner which chip to badge.
      isLocked
    }
  }
`;

export const createInventoryGql = gql`
  mutation createInventory($input: CreateInventoryInput!) {
    createInventory(input: $input) {
      inventoryId
      ndc
      productId
      drugName
      strength
      manufacturer
      isDefault
    }
  }
`;

export const setDefaultNdcForProductGql = gql`
  mutation setDefaultNdcForProduct($productId: String!, $ndc: String!) {
    setDefaultNdcForProduct(productId: $productId, ndc: $ndc)
  }
`;
// ─── POM-5974 / POM-5973 · Partner NDC preference ─────────────────────────────
// A preference is `partner + product -> exactly one preferred NDC`, and it is a
// HARD lock: when the preferred NDC is out of stock the order routes to review
// rather than falling back to another NDC. That is the opposite of the
// product-level default NDC (POM-5859), which is soft and falls through by FEFO.
//
// Two queries by design: the counts query drives the collapsed partner rows and
// the pagination, and a partner's preferences are fetched only when its row is
// expanded. Opening the tab does not pull every preference in the system.

// Parent rows. Note the filter is `partnerName` only — there is no free-text
// search across drug or NDC, so the toolbar searches partners, nothing else.
export const listPartnerNdcPreferenceCountsGql = gql`
  query listPartnerNdcPreferenceCounts(
    $filter: ListPartnerNdcPreferenceCountsFilter
    $sortBy: PartnerNdcPreferenceCountSortBy
    $limit: Int
    $offset: Int
  ) {
    listPartnerNdcPreferenceCounts(
      filter: $filter
      sortBy: $sortBy
      limit: $limit
      offset: $offset
    ) {
      result {
        partnerId
        partnerName
        preferenceCount
      }
      count
    }
  }
`;

// Children of one partner row. Always scoped by partnerUuid — this query is never
// used unscoped, because the counts query is what drives pagination.
export const listPartnerNdcPreferencesGql = gql`
  query listPartnerNdcPreferences(
    $filter: ListPartnerNdcPreferencesFilter
    $sortBy: PartnerNdcPreferenceSortBy
    $limit: Int
    $offset: Int
  ) {
    listPartnerNdcPreferences(filter: $filter, sortBy: $sortBy, limit: $limit, offset: $offset) {
      result {
        partnerId
        partnerName
        productId
        drugName
        ndc
        isActive
        createdTimestamp
        modifiedTimestamp
        strength
        dosageForm
        formAlias
        packSizeValue
        packSizeUnit
        inventoryDrugName
        manufacturer
      }
      count
    }
  }
`;

// Serves both "add" and "change": the pair is 1:1, so this is an upsert that
// deactivates the previous row. That also removes the add/change race — two
// people editing the same partner + product cannot hit a uniqueness error.
export const setPartnerNdcPreferenceGql = gql`
  mutation setPartnerNdcPreference($input: SetPartnerNdcPreferenceInput!) {
    setPartnerNdcPreference(input: $input) {
      partnerId
      productId
      ndc
      isActive
      modifiedTimestamp
    }
  }
`;

// Soft-clears the active preference so allocation falls back to default/FEFO.
// Returns a bare Boolean, and false is not an error: it means there was no active
// preference to clear, which happens when someone else removed it first. The
// caller has to tell those two outcomes apart rather than reporting success.
export const removePartnerNdcPreferenceGql = gql`
  mutation removePartnerNdcPreference($partnerUuid: UUID!, $productId: String!) {
    removePartnerNdcPreference(partnerUuid: $partnerUuid, productId: $productId)
  }
`;

// POM-5984 — return a lot's reserved (WIP) quantity to inventory. Omitting a narrowing means
// "everything at that level" and the server expands it; [] means nothing and is rejected.
export const recallLotAllocationsToInventoryGql = gql`
  mutation recallLotAllocationsToInventory($input: LotRecallScopeInput!) {
    recallLotAllocationsToInventory(input: $input) {
      status
      batchId
      lotsProcessed
      lineItemsAccepted
      quantityReturned
      skipped {
        lineItemId
        orderId
        reason
        detail
      }
    }
  }
`;
